import { defineStore } from "pinia";

import type { BaseBoard } from "@/board/BaseBoard";
import { cardSizeFrom } from "@/board/board-utils";
import { boardOf } from "@/board/boardFactory";
import type { BoardLocation } from "@/board/location/BoardLocation";
import { SelectionLocation } from "@/board/location/SelectionLocation";
import { emitStickyChanged, sendStickyNoteAction } from "@/composables/useEventBus";
import { divided } from "@/math/coordinates";
import { defaultCardSizeFactor } from "@/model/Settings";
import type { BoardType, Id } from "@/model/baseTypes";
import type { Board, BoardData } from "@/model/board";
import { isStickyBoard, MAX_FINISHED_TICKS } from "@/model/board";
import type { BoardCard, Card } from "@/model/card";
import type { RelativeCoordinate } from "@/model/coordinates";
import type { StickyType } from "@/model/stickyType";
import { startUserHeartbeat, stopUserHeartbeat } from "@/utils/heartbeat";

import { useBoardsStore } from "./boards";
import { usePointerStore } from "./pointer";
import { useUsersOnBoardStore } from "./usersOnBoard";

class BoardNotFoundError extends Error {}

export function isEmptyBoard(board: Board) {
  return board.id === "0";
}

export function emptyBoard(): BoardData<"team"> {
  return {
    id: "0",
    type: "team",
    team: { id: "", name: "empty" },
    cards: {},
    cardSize: cardSizeFrom({ factor: defaultCardSizeFactor }),
    maxZ: 0,
    state: { state: "loaded", finishedTicks: MAX_FINISHED_TICKS, loadedAt: 1 },
    iterationStates: [emptyIterationState(), emptyIterationState()],
    almSources: [],
    selected: {},
    shapes: [],
    objectives: [],
    stretchObjectives: [],
    objectivesResolved: true,
  };
}

export function emptyIterationState() {
  return { status: null, detail: null };
}

export function cardSize(): RelativeCoordinate {
  return useBoardStore().currentBoard().cardSize;
}

export function halfCardSize(): RelativeCoordinate {
  return divided(cardSize(), 2);
}

export const useBoardStore = defineStore("board", {
  state: () => ({
    board: undefined as Board | undefined,
    activeCardId: null as string | null,
    enlargedStickyNoteId: null as string | null,
    magnifying: false,
    scaleDropdownOpen: false,
    forceFullDetailsForBoardId: null as string | null,
  }),
  getters: {
    baseBoard: (state): BaseBoard<BoardType> => boardOf(state.board),
    positionalCardProperties() {
      return (pos: RelativeCoordinate): Partial<Card> =>
        this.baseBoard.positionalCardProperties(pos);
    },
    boardLocation() {
      return (pos: RelativeCoordinate | number | string, top?: number): BoardLocation =>
        this.baseBoard.location(pos, top);
    },
    // sticky types that can possibly appear on the board
    possibleStickyTypes(): StickyType[] {
      return this.baseBoard.possibleStickyTypes;
    },
    // sticky types that can be used to create a new sticky note
    creatableStickyTypes(): StickyType[] {
      return this.baseBoard.creatableStickyTypes;
    },
    showRiskyLinks(): boolean {
      return this.baseBoard.showRiskyLinks;
    },
    hasCurrentBoard: (state) => typeof state.board !== "undefined",
    currentBoard: (state) => (): Board => {
      if (state.board) {
        return state.board;
      }
      throw new BoardNotFoundError("No current board is set");
    },
    boardId() {
      return () => this.currentBoard().id;
    },
    isCurrentBoardFluid: (state) => isStickyBoard(state.board?.type),
    boardLocationById() {
      return (index: string): BoardLocation =>
        SelectionLocation.ofId(index) || this.boardLocation(index);
    },
    selectedStickyIds: (state) => Object.keys(state.board?.selected || {}),
    selectedStickies(): BoardCard[] {
      return this.selectedStickyIds.flatMap((id) =>
        this.currentBoard().cards[id] ? [this.currentBoard().cards[id]] : [],
      );
    },
    selectedOrActiveStickies(): BoardCard[] {
      if (this.selectedStickyIds.length > 0) {
        return this.selectedStickies;
      }
      const active = this.activeCard;
      return active ? [active] : [];
    },
    isStickySelected: (state) => {
      return (cardId: string) => cardId in (state.board?.selected || {});
    },
    isStickyIdSelectedOrActive: (state) => {
      return (id: string) => id in (state.board?.selected || {}) || state.activeCardId === id;
    },
    areMultipleStickiesSelected() {
      return this.selectedStickyIds?.length > 1;
    },
    activeCard(): BoardCard | null {
      const activeCardId = this.activeCardId;
      return activeCardId ? this.currentBoard().cards[activeCardId] : null;
    },
  },
  actions: {
    toggleStickyMagnify(magnify?: boolean) {
      this.magnifying = magnify === undefined ? !this.magnifying : magnify;
    },
    switchBoard(board?: Board) {
      const newBoard =
        board || (this.board && useBoardsStore().boardByTypeOptional(this.board.type));
      const res = { switched: false, board: this.board, cards: new Array<BoardCard>() };
      if (newBoard && newBoard.id !== this.board?.id) {
        res.switched = true;
        if (this.hasCurrentBoard) {
          res.cards = this.leaveBoard().cards;
        }
        this.board = newBoard;
        startUserHeartbeat();
      }
      return res;
    },
    leaveBoard() {
      const board = this.currentBoard();
      this.setActiveCardId(null);
      const cards = [];
      for (const id in board.cards) {
        const card = board.cards[id];
        if (card.meta.editing) {
          cards.push(card);
          card.meta.editing = false;
        }
      }
      stopUserHeartbeat();
      usePointerStore().reset();
      useUsersOnBoardStore().usersOnBoard = [];
      return { board, cards };
    },
    selectSticky(stickyId: string) {
      if (this.currentBoard().cards[stickyId]) {
        this.currentBoard().selected[stickyId] = true;
      }
    },
    setSelectedStickies(stickyIds: string[]) {
      this.currentBoard().selected = Object.fromEntries([...stickyIds].map((id) => [id, true]));
    },
    unselectSticky(stickyId: string, board?: Board) {
      delete (board ?? this.currentBoard()).selected[stickyId];
    },
    setActiveCardId(cardId: string | null) {
      const prev = this.activeCardId;
      if (prev !== cardId) {
        this.activeCardId = cardId;
        if (prev) {
          sendStickyNoteAction(prev, { action: "deactivate" });
          if (cardId === null) {
            emitStickyChanged(prev);
          }
        }
      }
    },
    setEnlargedStickyNoteId(id: string | null) {
      this.enlargedStickyNoteId = id;
    },
    setActiveCard(e: Id & { active: boolean }) {
      // don't clear active card if a different one is active
      if (!e.active && this.activeCardId !== e.id) {
        return;
      }
      this.setActiveCardId(e.active ? e.id : null);
    },
    setScaleDropdownOpen(open: boolean) {
      this.scaleDropdownOpen = open;
    },
  },
});
